import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import dayjs from "dayjs";
import { Button } from "primereact/button";

import { requestEnterprise } from "../../../../store/slices/globalSlice";
import "./preview-certificate.scss";

// The system landscape designs have their seal at the lower left, level with
// the item line: there the line keeps to the middle 56% of the certificate,
// like the PDFs of a certificate download (API CertificateLayout).
const SEAL_BESIDE_ITEM_DESIGNS = [
  "images/Default-Modern-Landscape.jpg",
  "images/Default-Vivid-Landscape.jpg",
];

// Text never shrinks below this share of its size (API CertificateLayout.MinTextScale).
const MIN_TEXT_SCALE = 0.6;
const FULL_SIZE = { scale: 1, wrap: false };

// How a line of naturalWidth px fits a boxWidth px box, as on the PDFs (API
// CertificatePdfRenderer): at its size, or smaller down to 60%, or on two
// lines at 60%.
export const fitLine = (naturalWidth, boxWidth) => {
  if (!naturalWidth || !boxWidth || naturalWidth <= boxWidth) {
    return FULL_SIZE;
  }
  const scale = boxWidth / naturalWidth;
  return scale >= MIN_TEXT_SCALE
    ? { scale, wrap: false }
    : { scale: MIN_TEXT_SCALE, wrap: true };
};

// Refresh the signed logo link this long before it expires.
const LOGO_REFRESH_MARGIN_MS = 60 * 1000;

// Milliseconds until the signed logo link should be refreshed, or null when it carries no expiry.
const msUntilLogoRefresh = (logo) => {
  try {
    const expires = Number(
      new URL(logo, process.env.REACT_APP_URL).searchParams.get("expires")
    );
    if (!expires) return null;
    return Math.max(0, expires * 1000 - Date.now() - LOGO_REFRESH_MARGIN_MS);
  } catch {
    return null;
  }
};

// Printing before the images finish loading leaves them out of the printed certificate.
const waitForImages = (container) =>
  Promise.all(
    Array.from(container?.querySelectorAll("img") || []).map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            img.addEventListener("load", resolve, { once: true });
            img.addEventListener("error", resolve, { once: true });
          })
    )
  );

export const PreviewCertificate = ({
  url,
  user,
  learningName = "Learning Program name",
  dateofCompletion = new Date(),
  orientation = "portrait",
  isActual = false,
  enterprise,
  selectedCertificate,
  refreshLogo = () => {},
}) => {
  const intl = useIntl();
  const certificateRef = useRef(null);
  // A failed logo load gets one automatic retry with a fresh link.
  const [logoRetried, setLogoRetried] = useState(false);
  const name = `${user.firstName} ${user.lastName ? ` ${user.lastName}` : ""}`;
  const { logo } = enterprise;
  const sealSafe = SEAL_BESIDE_ITEM_DESIGNS.includes(url);
  const itemRef = useRef(null);
  const [itemFit, setItemFit] = useState(FULL_SIZE);

  // The seal-safe item line is narrow: measure the name on one line at its
  // full size and fit it. Measured again once the certificate font has
  // loaded, whenever the box changes size (a resized window, a wider or
  // narrower dialog) and for printing, whose page gives the box another width.
  useLayoutEffect(() => {
    const box = itemRef.current;
    const text = box?.firstElementChild;
    if (!sealSafe || !text) {
      setItemFit(FULL_SIZE);
      return undefined;
    }
    let active = true;
    const measure = (now = false) => {
      if (!active) return;
      const { fontSize, whiteSpace } = text.style;
      text.style.fontSize = "1em";
      text.style.whiteSpace = "nowrap";
      const fit = fitLine(text.getBoundingClientRect().width, box.clientWidth);
      text.style.fontSize = fontSize;
      text.style.whiteSpace = whiteSpace;
      const apply = () =>
        setItemFit((current) =>
          current.scale === fit.scale && current.wrap === fit.wrap ? current : fit
        );
      // The browser lays out the printed page right after the print events:
      // the new size must be on the page by then.
      if (now) flushSync(apply);
      else apply();
    };
    measure();
    document.fonts?.ready?.then(() => measure());

    // Only a change of width changes the fit (refitting can change the height).
    let width = box.clientWidth;
    const observer =
      typeof window.ResizeObserver === "function"
        ? new window.ResizeObserver(() => {
            if (box.clientWidth === width) return;
            width = box.clientWidth;
            measure();
          })
        : null;
    observer?.observe(box);

    const measureForPrint = () => measure(true);
    window.addEventListener("beforeprint", measureForPrint);
    window.addEventListener("afterprint", measureForPrint);
    const printQuery = window.matchMedia?.("print");
    printQuery?.addEventListener?.("change", measureForPrint);

    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener("beforeprint", measureForPrint);
      window.removeEventListener("afterprint", measureForPrint);
      printQuery?.removeEventListener?.("change", measureForPrint);
    };
  }, [sealSafe, learningName]);

  // The logo link is signed and expires; while the certificate is open, fetch a fresh link before it does.
  useEffect(() => {
    const delay = msUntilLogoRefresh(logo);
    if (delay === null) return undefined;
    const timer = setTimeout(() => refreshLogo(enterprise?.subDomain), delay);
    return () => clearTimeout(timer);
  }, [logo, enterprise?.subDomain, refreshLogo]);

  const handleLogoError = () => {
    if (!logoRetried) {
      setLogoRetried(true);
      refreshLogo(enterprise?.subDomain);
    }
  };

  const downloadCertificate = async () => {
    await waitForImages(certificateRef.current);
    window.print();
  };

  return (
    <div
      className="edexlms-certificate"
      id="edexlms_certificate"
      ref={certificateRef}
    >
      <img
        className={`cert-image ${orientation}`}
        src={`${new URL(url, process.env.REACT_APP_URL).toString()}?v=${
          selectedCertificate?.updatedAt || process.env.REACT_APP_VERSION
        }`}
        alt="certificate"
      />
      <div className={`edexlms-certificate-${orientation}`}>
        <div className="cert-logo">
          <img
            src={`${new URL(logo, process.env.REACT_APP_URL).toString()}`}
            alt="Logo"
            className="logo"
            onError={handleLogoError}
          />
        </div>
        <div className="cert-name">{name}</div>
        <div
          ref={itemRef}
          className={`cert-program${sealSafe ? " cert-program-seal-safe" : ""}`}
        >
          <span
            className={
              (sealSafe && itemFit.wrap && "cert-program-text-wrapped") ||
              undefined
            }
            style={
              (sealSafe && {
                fontSize: `${itemFit.scale}em`,
                whiteSpace: itemFit.wrap ? "normal" : "nowrap",
              }) ||
              undefined
            }
          >
            {learningName}
          </span>
        </div>
        <div className="cert-date">
          {dayjs(dateofCompletion).format("DD MMM YYYY")}
        </div>
      </div>
      {(isActual && (
        <div className="cert-download no-print">
          <Button
            icon="pi pi-arrow-circle-down"
            onClick={downloadCertificate}
            aria-label={intl.formatMessage({ id: "certificate_download" })}
            outlined
          ></Button>
        </div>
      )) ||
        null}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.self?.user,
  enterprise: state?.global?.enterprise,
});

const mapDispatchToProps = (dispatch) => ({
  refreshLogo: (domain) => dispatch(requestEnterprise({ domain })),
});

export default connect(mapStateToProps, mapDispatchToProps)(PreviewCertificate);
